/**
 * The anticipated-item vocabulary, kept free of any runtime dependency.
 *
 * Split out of `quoteIntakeService` because that module's first line imports
 * `apiClient`, which reads `import.meta.env` and therefore only loads under
 * Vite. These three declarations are pure data and one comparison, and the
 * slot algebra that consumes them has to be testable outside the bundler.
 *
 * `quoteIntakeService` re-exports all of it, so every existing import site is
 * unchanged.
 */

/** How an anticipated item is supplied (spec 13810fd2). */
export type QuoteIntakeItemKind = "file" | "text";

/** One thing the conversation suggests providing (spec c04ed435 §5b). */
export interface QuoteIntakeAnticipatedDocument {
  /** Shown to the consumer, in their own words. */
  label: string;
  /**
   * 'file' attaches a document, 'text' types a short value — an RUT, a
   * padrón, a date. Absent on items drafted before spec 13810fd2, which were
   * all file slots; read it through `anticipatedItemKind` rather than
   * comparing it, or those items render no control at all.
   */
  kind?: QuoteIntakeItemKind;
  /**
   * Null when no platform document type fits — most bespoke notarial work.
   * Those uploads fall back to the generic type on the server. Always null on
   * a text item: nothing is filed, so there is nothing to type.
   */
  documentType: string | null;
  why: string;
}

/**
 * The ONE place `kind` is read. Never compare the raw field: an item from an
 * older draft has no `kind`, and an unknown value is equally possible, so both
 * land on the control those items already had.
 */
export function anticipatedItemKind(
  doc: QuoteIntakeAnticipatedDocument,
): QuoteIntakeItemKind {
  return doc.kind === "text" ? "text" : "file";
}
